import { useCart } from 'components/Cart';
import { navigate } from 'gatsby';
import { useEffect, useRef, useState } from 'react';
import { addLineItem, applyPromoCode } from 'utils/api/cartApis';
import { CHECKOUT_BASE_PATH } from 'utils/constants/checkout';
import { logError } from 'utils/helpers';

export const PROMOCODE_PARAM = 'promo-code';

/**
 * Update the order with the line items and promo code present in the url params.
 * @returns
 */
export default function useUpdateOrderWithUrlParams() {
  // we make sure this only runs once
  const didRun = useRef(false);
  const { order, refetchOrder } = useCart();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!order || didRun.current) {
      return;
    }
    didRun.current = true;
    setIsLoading(true);

    const main = async () => {
      const { search } = window.location;
      const { lineItems, promoCode } = parseBundleItemsParams(search);

      // add line items to the cart
      for (const lineItem of lineItems) {
        await addLineItem({
          orderId: order.id,
          lineItem: {
            variant_id: lineItem.id,
            quantity: lineItem.quantity,
          },
        });
      }

      // apply promo code if present
      if (promoCode) {
        await applyPromoCode({
          orderId: order.id,
          code: promoCode,
        });
      }
    };

    main()
      .catch((error) => {
        logError('Error adding line items to the cart', {
          component: 'CheckoutMLContainer',
          method: 'DefaultRoute',
          error,
        });
      })
      .finally(() => {
        refetchOrder?.();
        setIsLoading(false);
        navigate(`${CHECKOUT_BASE_PATH}/details`, { replace: true });
      });
  }, [order, refetchOrder]);

  return { isLoading };
}

/**
 * Parse the given url params into valid line items payload.
 * Then add the line items to the cart.
 *
 * @example
 *  Given a url param like: ?123:4&456:2&promo-code=TEST
 *  Then the line items payload will be:
 *  {
 *   line_items: [
 *    { variant_id: 123, quantity: 4 },
 *   { variant_id: 456, quantity: 2 }
 *  ],
 * promo_code: 'TEST'
 * }
 *
 *
 * @returns
 */

const BUNDLE_ITEM_PARAM_REGEX = /^(\d+):(\d+)$/g;

type ParseBundleItemsParamsResult = {
  lineItems: { id: number; quantity: number }[];
  promoCode: string | null;
};

/**
 * Parse the given url params into valid line items payload.
 * @param search
 * @returns
 */
export function parseBundleItemsParams(
  search: string,
): ParseBundleItemsParamsResult {
  const result: ParseBundleItemsParamsResult = {
    lineItems: [],
    promoCode: null,
  };

  const urlParams = new URLSearchParams(search);

  urlParams.forEach((value, key) => {
    if (key === PROMOCODE_PARAM) {
      result.promoCode = value;
      return;
    }

    // this way we make sure we only parse valid bundle items
    const match = key.matchAll(BUNDLE_ITEM_PARAM_REGEX).next();

    if (!match.value) {
      return;
    }

    const [, variantId, quantity] = match.value;

    result.lineItems.push({
      id: parseInt(variantId, 10),
      quantity: parseInt(quantity, 10),
    });
  });

  return result;
}

export function bundleItemsParamsToString(
  lineItems: { variant_id: number; quantity: number }[],
  promoCode: string | null,
): string {
  const params = new URLSearchParams();

  for (const lineItem of lineItems) {
    params.append(`${lineItem.variant_id}:${lineItem.quantity}`, '');
  }

  if (promoCode) {
    params.append(PROMOCODE_PARAM, promoCode);
  }

  return params.toString();
}
